import { getAppHref, getClerkSignInUrl, getClerkSignUpUrl } from "@/lib/env";

/**
 * Platform access links. Self-serve signup is the product path; Clerk owns
 * account creation and the dashboard owns organization/product onboarding.
 */

export type SiteLocale = "sv" | "en";

/**
 * The one switch for every website call to action that opens the Nacka demo.
 *
 * Owner 2026-11-05, verbatim: "Demo som tar en minut samt saknad Nacka caire är
 * ett fel. Cta behövde tas bort tills demo laddar lika snabbt som övriga
 * webbsidor."
 *
 * Off hides the calls to action only: the demo, its route and the redirects in
 * `redirectMap.ts` keep working. Set to `true` to restore them once the demo
 * loads as fast as the website.
 */
export const PUBLIC_NACKA_DEMO_CTA_ENABLED: boolean = false;

/** One DB-backed public demo in the dashboard; no website schedule replica. */
export function publicNackaDemoHref(locale: SiteLocale): string {
  return getAppHref(`/demo/nacka?locale=${locale}`);
}

export const interactiveDemoPaths: Record<SiteLocale, string> = {
  sv: "/produkt/interaktiv-demo",
  en: "/en/product/interactive-demo",
};

export function interactiveDemoHref(locale: SiteLocale): string {
  return interactiveDemoPaths[locale];
}

export function interactiveDemoCtaLabel(locale: SiteLocale): string {
  return locale === "en" ? "Try the interactive demo" : "Testa interaktiv demo";
}

export const CONTACT_US_HREF = "mailto:info@caire.se";
export const CONTACT_US_TARGET = "_blank";
export const CONTACT_US_REL = "noopener noreferrer";

export function contactCtaLabel(locale: SiteLocale): string {
  return locale === "en" ? "Contact us" : "Kontakta oss";
}

export const bookDemoPaths: Record<SiteLocale, string> = {
  sv: "/boka-demo",
  en: "/en/book-demo",
};

/**
 * Calendar-first booking page. Visitors clicking a "Boka demo" CTA expect a
 * calendar, so this is a dedicated page with the embed above the fold rather
 * than the full contact page.
 */
export function bookDemoHref(locale: SiteLocale): string {
  return bookDemoPaths[locale];
}

/**
 * Book-demo URL with a stable `source` query for CRM / analytics attribution
 * (e.g. carefox, jamforelse-schemalaggning, planeringsverktyg).
 *
 * `source` ONLY — deliberately no `utm_*`. These links are INTERNAL: they are
 * emitted on our own comparison and onboarding pages and point at our own
 * /boka-demo. GA4 starts a NEW session whenever a link carries campaign
 * parameters and re-stamps that session's traffic source, so a visitor who
 * arrived from Google, LinkedIn or sverigeshemtjanst.se was being re-attributed
 * to `organic / caire_site` the moment they clicked a demo CTA — the acquisition
 * source was overwritten by our own navigation. `source` is a plain query
 * parameter, carries the same CRM attribution, and GA4 does not read it as a
 * campaign, so it is safe on an internal link.
 *
 * utm_* belongs on links we place OFF this site (ads, newsletters, partners),
 * where there is a real external source to record.
 */
export function bookDemoHrefWithSource(
  locale: SiteLocale,
  source: string,
): string {
  const params = new URLSearchParams({ source });
  return `${bookDemoHref(locale)}?${params.toString()}`;
}

/** Booking CTAs open in a new tab so the visitor keeps the page they were reading. */
export const BOOK_DEMO_TARGET = "_blank";
export const BOOK_DEMO_REL = "noopener noreferrer";

export function bookDemoCtaLabel(locale: SiteLocale): string {
  return locale === "en" ? "Book a walkthrough" : "Boka genomgång";
}

/** Destination for the primary self-serve acquisition CTA. */
export const platformSignupHref = getClerkSignUpUrl();

/** Returning users enter Clerk sign-in; acquisition keeps the signup helpers. */
export const platformSignInHref = getClerkSignInUrl();

const PLATFORM_PRODUCTS_RETURN_TO = "/products/manage";

/**
 * Canonical self-serve entry for product acquisition. The fixed relative
 * redirect is accepted by dashboard auth without allowing an external return
 * target. New users still complete organization provisioning before the
 * product catalog; existing signed-in users can continue there directly.
 */
export function platformProductSignupHref(): string {
  return `${platformSignupHref}?redirect=${encodeURIComponent(PLATFORM_PRODUCTS_RETURN_TO)}`;
}

/** Label for the primary self-serve acquisition CTA, by locale. */
export function platformSignupLabel(locale: SiteLocale): string {
  return locale === "en" ? "Sign up / Log in" : "Skapa konto / Logga in";
}

/** Label for the returning-user sign-in link beside the analysis CTA. */
export function platformNavAuthLabel(locale: SiteLocale): string {
  return locale === "en" ? "Log in" : "Logga in";
}

export function platformSignupCtaId(): string {
  return "platform_auth";
}
